import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import Checkbox from '@material-ui/core/Checkbox';
import { ThemeProvider } from '@material-ui/core/styles';

import checkboxTheme from '../../../../shared/theme/checkboxTheme';

const CheckboxRow = styled('div')`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const StyledCheckbox = styled(Checkbox)`
  padding: 4px;

  &:hover {
    background-color: rgba(119, 167, 255, 0.04);
  }
`;

const CheckboxLabel = styled('label')`
  margin-bottom: 0;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const UncheckedIcon = () => {
  const { colors } = useTheme();
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect x='0.5' y='0.5' width='17' height='17' rx='1.5' stroke={colors.borderLight} />
    </svg>
  );
};

const CheckedIcon = () => {
  const { colors } = useTheme();
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect x='0.5' y='0.5' width='17' height='17' rx='1.5' fill={colors.surface} stroke={colors.hover} />
      <path d='M5 9L8 12L13 6' stroke={colors.hover} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );
};

const IndeterminateIcon = () => {
  const { colors } = useTheme();
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect x='0.5' y='0.5' width='17' height='17' rx='1.5' fill={colors.surface} stroke={colors.hover} />
      <path d='M5 9H13' stroke={colors.hover} strokeWidth='2' strokeLinecap='round' />
    </svg>
  );
};

/** The bare MUI checkbox. color='default' plus the theme keep it from falling
 * back to MUI's secondary color, whose checked ripple and hover come out pink. */
export const CheckboxControl = ({
  id, checked, indeterminate, onChange, inputProps,
}) => (
  <ThemeProvider theme={checkboxTheme}>
    <StyledCheckbox
      checked={checked}
      checkedIcon={<CheckedIcon />}
      color='default'
      icon={<UncheckedIcon />}
      id={id}
      indeterminate={indeterminate}
      indeterminateIcon={<IndeterminateIcon />}
      inputProps={inputProps}
      onChange={onChange}
    />
  </ThemeProvider>
);

const CopyCheckbox = ({
  id, checked, indeterminate, onChange, label, inputProps,
}) => (
  <CheckboxRow>
    <CheckboxControl
      checked={checked}
      id={id}
      indeterminate={indeterminate}
      inputProps={inputProps}
      onChange={onChange}
    />
    <CheckboxLabel htmlFor={id}>{label}</CheckboxLabel>
  </CheckboxRow>
);

CheckboxControl.propTypes = {
  id: PropTypes.string,
  checked: PropTypes.bool.isRequired,
  indeterminate: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  inputProps: PropTypes.shape({}),
};

CheckboxControl.defaultProps = {
  id: undefined,
  indeterminate: false,
  inputProps: undefined,
};

CopyCheckbox.propTypes = {
  id: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  indeterminate: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.node.isRequired,
  inputProps: PropTypes.shape({}),
};

CopyCheckbox.defaultProps = {
  indeterminate: false,
  inputProps: undefined,
};

export default CopyCheckbox;
